import { Location } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { WindowEnums } from 'src/app/enum/window-enums';
import { OptionWindowModel } from 'src/app/models/authentication/option-window.model';
import { UserModel } from 'src/app/models/authentication/user.model';
import { AccountingModelModel } from 'src/app/models/management/accounting-model.model';
import { OptionWindowService } from 'src/app/services/authentication/option-window.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { AccountingModelService } from 'src/app/services/management/accounting-model.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-accounting-model',
  templateUrl: './register-accounting-model.component.html',
  styleUrls: ['./register-accounting-model.component.scss'],
  standalone: false
})
export class RegisterAccountingModelComponent implements OnInit {

  private readonly accountingModelService = inject(AccountingModelService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly location = inject(Location);
  private readonly optionWindowService = inject(OptionWindowService);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly userAuthenticatedService = inject(UserAuthenticatedService);
  private readonly windowService = inject(WindowService);

  accountingModelContract: AccountingModelModel = new AccountingModelModel();
  accountingModelId: number = 0;
  disabledDetail: boolean = true;
  disabledForm: boolean = false;
  formAccountingModel: FormGroup = new FormGroup({});
  permissionUpdatable: boolean = false;
  permissionsUser: Array<OptionWindowModel> = new Array<OptionWindowModel>();
  updateListDetail: boolean = false;
  url: string = 'accounting-model';
  userAuthenticated: UserModel = new UserModel();

  async ngOnInit() {
    this.validateForm(this.accountingModelContract);
    await this.getPermission();
    this.activatedRoute.params.subscribe(params => {
      this.accountingModelId = Object.keys(params).length > 0 ? params?.id : null;
    });
    this.handleInitialState();
  }

  async changeConsecutive() {
    let consecutive = this.formAccountingModel.get('consecutive')?.value;

    if (consecutive < 0 || consecutive === null || consecutive.toString().trim() === '') {
      consecutive = 0;
    }

    if (consecutive === 0) {
      this.createAccountingModel();
    } else {
      try {
        this.accountingModelContract = await firstValueFrom(
          this.accountingModelService.getAccountingModelByConsecutive(consecutive)
        );
        this.accountingModelId = this.accountingModelContract.accountingModelId;
        this.validateForm(this.accountingModelContract);
        this.location.replaceState(`/accounting-model/register/${this.accountingModelId}`);
      } catch (error) {
        console.error('Error al obtener el modelo contable por consecutivo.', error);
        this.createAccountingModel();
      }
    }
  }

  createAccountingModel() {
    this.accountingModelContract = new AccountingModelModel();
    this.accountingModelContract.consecutive = 0;
    this.accountingModelId = 0;
    this.validateForm(this.accountingModelContract);
    this.disabledDetail = true;
    setTimeout(() => this.formAccountingModel.controls['consecutive'].disable(), 100);
    this.location.replaceState('/accounting-model/register');
  }

  async deleteAccountingModel() {
    if (this.accountingModelContract.accountingModelId === 0 || this.accountingModelContract.accountingModelId == null) {
      this.toastHandlerService.showToast('warning', 'Debe crear un modelo contable.');
      return;
    }
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        'El usuario no tiene permisos para eliminar modelos contables. Comuníquese con el administrador del sistema.'
      );
      return;
    }
    const accepted = await this.confirmDialogService.confirm('¿Realmente desea eliminar el modelo contable actual?');
    if (accepted) {
      try {
        await firstValueFrom(this.accountingModelService.deleteAccountingModel(this.accountingModelId));
        this.createAccountingModel();
        this.toastHandlerService.showToast('success', 'El modelo contable fue eliminado exitosamente.');
      } catch (error) {
        console.error('Error al eliminar el modelo contable.', error);
      }
    }
  }

  async getButtonClick(buttonClick: any) {
    let consecutive = this.formAccountingModel.get('consecutive')?.value;

    if (consecutive < 0 || consecutive === '' || consecutive == null) {
      consecutive = 0;
    }

    switch (buttonClick.target.id) {
      case 'first': {
        try {
          this.accountingModelContract = await firstValueFrom(this.accountingModelService.getFirstAccountingModelByKeysAsync());
          this.validateForm(this.accountingModelContract);
          this.accountingModelId = this.accountingModelContract.accountingModelId;
          this.location.replaceState(`/accounting-model/register/${this.accountingModelId}`);
        } catch (error) {
          console.error('Error al obtener el primer modelo contable.', error);
        }
        break;
      }
      case 'previus': {
        try {
          this.accountingModelContract = await firstValueFrom(
            this.accountingModelService.getPreviusAccountingModelByKeysAsync(consecutive)
          );
          this.validateForm(this.accountingModelContract);
          this.accountingModelId = this.accountingModelContract.accountingModelId;
          this.location.replaceState(`/accounting-model/register/${this.accountingModelId}`);
        } catch (error) {
          console.error('Error al obtener el modelo contable anterior.', error);
        }
        break;
      }
      case 'next': {
        try {
          this.accountingModelContract = await firstValueFrom(
            this.accountingModelService.getNextAccountingModelByKeysAsync(consecutive)
          );
          this.validateForm(this.accountingModelContract);
          this.accountingModelId = this.accountingModelContract.accountingModelId;
          this.location.replaceState(`/accounting-model/register/${this.accountingModelId}`);
        } catch (error) {
          console.error('Error al obtener el modelo contable siguiente.', error);
        }
        break;
      }
      case 'last': {
        try {
          this.accountingModelContract = await firstValueFrom(this.accountingModelService.getLastAccountingModelByKeysAsync());
          this.validateForm(this.accountingModelContract);
          this.accountingModelId = this.accountingModelContract.accountingModelId;
          this.location.replaceState(`/accounting-model/register/${this.accountingModelId}`);
        } catch (error) {
          console.error('Error al obtener el último modelo contable.', error);
        }
        break;
      }
    }
  }

  async getAccountingModelById() {
    try {
      this.accountingModelContract = await firstValueFrom(this.accountingModelService.getAccountingModelById(this.accountingModelId));
      this.accountingModelId = this.accountingModelContract.accountingModelId;
      this.validateForm(this.accountingModelContract);
      this.location.replaceState(`/accounting-model/register/${this.accountingModelId}`);
    } catch (error) {
      console.error('Error al obtener el modelo contable por ID.', error);
    }
  }

  getUpdateListDetail() {
    this.updateListDetail = !this.updateListDetail;
  }

  async getPermission() {
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
      this.getPermissionsForUserByWindow();
    } catch (error) {
      console.error('Error al obtener los permisos del perfil.', error);
    }
  }

  async getPermissionsForUserByWindow() {
    this.userAuthenticated = await firstValueFrom(
      this.userAuthenticatedService.userObservable.pipe(filter(user => user && user.userId !== 0))
    );
    this.permissionsUser = await firstValueFrom(
      this.optionWindowService.getPermissionsForUserByWindow(WindowEnums.AccountingModel)
    );
  }

  async handleInitialState() {
    if (this.accountingModelId !== null && this.accountingModelId !== 0) {
      await this.getAccountingModelById();
    } else {
      this.createAccountingModel();
    }
  }

  async send() {
    if (!this.formAccountingModel.valid) {
      this.showValidationMessages();
      return;
    }
    const model = this.formAccountingModel.getRawValue() as AccountingModelModel;

    if (this.accountingModelId === 0) {
      try {
        this.accountingModelContract = await firstValueFrom(this.accountingModelService.createAccountingModel(model));
        this.accountingModelId = this.accountingModelContract.accountingModelId;
        this.validateForm(this.accountingModelContract);
        this.location.replaceState(`/accounting-model/register/${this.accountingModelId}`);
        this.toastHandlerService.showToast('success', 'El modelo contable fue creado exitosamente.');
      } catch (error) {
        console.error('Error al crear el modelo contable.', error);
      }
    } else {
      this.accountingModelContract.description = model.description;
      try {
        this.accountingModelContract = await firstValueFrom(
          this.accountingModelService.updateAccountingModel(this.accountingModelContract)
        );
        this.validateForm(this.accountingModelContract);
        this.toastHandlerService.showToast('success', 'El modelo contable fue modificado exitosamente.');
      } catch (error) {
        console.error('Error al actualizar el modelo contable.', error);
      }
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      consecutive: {
        required: 'El campo Modelo es obligatorio.',
        min: 'Debe ingresar un Modelo válido.'
      },
      description: {
        maxlength: 'La descripción no puede tener más de 250 caracteres.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formAccountingModel.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  updateFormState() {
    if (this.permissionUpdatable) {
      this.disabledDetail = this.accountingModelContract.accountingModelId === 0;
      this.disabledForm = false;
      setTimeout(() => this.formAccountingModel.enable(), 100);
    } else {
      this.disabledForm = true;
      this.disabledDetail = true;
      setTimeout(() => this.formAccountingModel.disable(), 100);
      setTimeout(() => this.formAccountingModel.controls['consecutive'].enable(), 100);
    }
  }

  validateForm(accountingModel: AccountingModelModel) {
    this.formAccountingModel = this.formBuilder.group({
      consecutive: [accountingModel.consecutive, [Validators.required, Validators.min(0)]],
      description: [accountingModel.description, [Validators.maxLength(250)]]
    });
    this.updateFormState();
  }

}
